import React, { useState, useEffect } from 'react';
import { Store, Calendar, Tag, DollarSign, Layers, UploadCloud, AlertCircle, CheckCircle2, Hash } from 'lucide-react';

// Lista de contas contábeis — fonte única em `pettycash-contas.js` (era copiada
// aqui e na Auditoria; ver o aviso lá sobre renomear conta já usada).
import { categoriasDespesa } from './pettycash-contas';
// Vídeo do procedimento embutido na tela (componente compartilhado).
// ⚠️ O `apiBase` é o proxy do MÓDULO dono do vídeo — cada módulo tem o seu.
import VideoProcedimento from '../../VideoProcedimento';
import EscolherAnexo from '../../EscolherAnexo';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
// Quem é do escritório e quem é da loja — fonte única das duas telas do Petty
// Cash (ver o porquê, e o bug que ela conserta, no próprio arquivo).
import { PERFIS_VISAO_TOTAL } from './pettycash-perfis';

// Proxy de vídeo do módulo Financeiro (catálogo em dtbVideoProcedimento).
const API_VIDEOS = '/api/financeiro/videos';
// Teal do Financeiro (docs/ARQUITETURA.md › Paleta dos módulos).
const COR_FINANCEIRO = '#16a39c';

const FinanceiroPettyCashFormulario = () => {
    // 1. DADOS DO USUÁRIO PARA A TRAVA
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Quem NÃO enxerga a rede lança sempre para a própria loja: o seletor abre
    // preenchido e desabilitado. Mesma regra (e mesma lista) do Controle ao lado.
    // ⚠️ Era `user?.perfil === 'Gerente' || 'Gerente Plantao'`, que valia `true`
    //    para TODO MUNDO — o `||` comparava com uma string não-vazia. O Admin e o
    //    Financeiro ficavam travados na loja do cadastro deles, sem erro nenhum.
    const podeEscolherLoja = PERFIS_VISAO_TOTAL.includes(perfilNoModulo(user?.perfil, 'financeiro'));
    const travadoNaLoja = !podeEscolherLoja;

    // Limite mensal vem do backend por loja (fonte única da verdade em
    // server-pettycash.js). Inicia no padrão da rede e é atualizado ao
    // escolher a loja, pois há lojas com teto diferente (ex.: PCI = R$ 700).
    const [limiteMensal, setLimiteMensal] = useState(600.00);

    const [lojas, setLojas] = useState([]);
    const [despesasDoMes, setDespesasDoMes] = useState(0); 
    const [isCarregando, setIsCarregando] = useState(false);
    const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
    
    const saldoRestante = limiteMensal - despesasDoMes;
    const isBloqueado = despesasDoMes >= limiteMensal;
    const porcentagemGasta = (despesasDoMes / limiteMensal) * 100;

    const hoje = new Date();
    const primeiroDiaDoMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];


    // Preso à própria loja: já inicia com a loja do cadastro preenchida
    const criarFormInicial = () => ({
        loja: travadoNaLoja && user.loja ? user.loja : '',
        data: '',
        contaDespesa: '',
        numeroCupom: '',
        valor: '',
        quantidade: 1,
        cupom: null
    });

    const [formData, setFormData] = useState(criarFormInicial);

    useEffect(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken'); 
        fetch('/api/pettycash/lojas', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
        .then(response => { if (!response.ok) throw new Error('Falha ao carregar a lista de lojas.'); return response.json(); })
        .then(data => { if (Array.isArray(data)) setLojas(data); })
        .catch(error => { setMensagem({ tipo: 'erro', texto: error.message }); setLojas([]); });
    }, []);

    // Atualiza saldo sempre que a loja for escolhida (ou se for o gerente logo ao abrir)
    useEffect(() => {
        if (!formData.loja) { setDespesasDoMes(0); return; }
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        fetch(`/api/pettycash/saldo/${encodeURIComponent(formData.loja)}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
        .then(response => { if (!response.ok) throw new Error('Falha ao buscar o saldo.'); return response.json(); })
        .then(data => {
            if (data.totalGasto !== undefined) setDespesasDoMes(data.totalGasto);
            if (data.limiteMensal !== undefined) setLimiteMensal(data.limiteMensal);
        })
        .catch(err => console.error("Erro ao buscar saldo", err));
    }, [formData.loja]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleSubmit = async (e) => {
        e.preventDefault();

        // O cupom era obrigatório pelo `required` do <input type="file">. Com os
        // dois gatilhos de anexo o input não está mais no formulário, então a
        // obrigatoriedade passou a ser checada aqui — sem isso a despesa subiria
        // sem comprovante.
        if (!formData.cupom) {
            setMensagem({ tipo: 'erro', texto: 'Anexe o cupom fiscal antes de enviar.' });
            return;
        }

        setIsCarregando(true);
        setMensagem({ tipo: '', texto: '' });

        const despesaSelecionada = categoriasDespesa.find(c => c.conta === formData.contaDespesa);

        const dadosForm = new FormData();
        dadosForm.append('loja', formData.loja);
        dadosForm.append('dataCompra', formData.data);
        dadosForm.append('descricao', despesaSelecionada ? despesaSelecionada.desc : '');
        dadosForm.append('contaDespesa', formData.contaDespesa);
        dadosForm.append('numeroCupom', formData.numeroCupom); 
        dadosForm.append('valor', formData.valor);
        dadosForm.append('quantidade', formData.quantidade);
        if (formData.cupom) dadosForm.append('cupom', formData.cupom); 

        const token = localStorage.getItem('token') || localStorage.getItem('authToken');

        try {
            let response = await fetch('/api/pettycash/submit', {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: dadosForm
            });
            let data = await response.json();

            // 409 + duplicidade: o servidor achou lançamento parecido nesta loja e parou
            // ANTES de gravar (nada subiu pro SharePoint). Mostramos o que ele achou —
            // com quem lançou e quando, que é o dado que resolve a dúvida — e o usuário
            // decide. Confirmando, reenviamos o MESMO FormData com a marca de confirmação.
            // O arquivo continua em memória no formData.cupom, então não há reanexo.
            if (response.status === 409 && data?.duplicidade) {
                const lista = (data.possiveisDuplicatas || []).map(d =>
                    `• ${d.DESCRICAO} — R$ ${parseFloat(d.VALOR).toFixed(2)} — compra ${d.DATA_COMPRA}` +
                    ` — cupom ${d.NUMERO_CUPOM || '—'}` +
                    `\n   lançado por ${d.LANCADO_POR || 'usuário desconhecido'} em ${d.DATA_REGISTRO}`
                ).join('\n');

                const querConfirmar = window.confirm(
                    `Já existe lançamento parecido nesta loja:\n\n${lista}\n\n` +
                    'Confira se não é a mesma despesa lançada duas vezes — ela pode ter sido registrada por outra pessoa da loja.\n\n' +
                    'OK = lançar mesmo assim\nCancelar = não lançar'
                );

                if (!querConfirmar) {
                    setMensagem({ tipo: 'erro', texto: 'Lançamento cancelado. Nada foi registrado.' });
                    setIsCarregando(false);
                    return;
                }

                dadosForm.append('confirmarDuplicidade', 'true');
                response = await fetch('/api/pettycash/submit', {
                    method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: dadosForm
                });
                data = await response.json();
            }

            if (response.ok) {
                const valorLancado = parseFloat(formData.valor) || 0;

                setMensagem({ tipo: 'sucesso', texto: 'Despesa enviada com sucesso!' });
                setFormData(criarFormInicial());
                setDespesasDoMes(prev => prev + valorLancado);
            } else {
                setMensagem({ tipo: 'erro', texto: data.error || 'Erro ao enviar despesa.' });
            }
        } catch (error) {
            setMensagem({ tipo: 'erro', texto: 'Falha na conexão com o servidor.' });
        } finally {
            setIsCarregando(false);
        }
    };

    const inputContainerStyle = { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' };
    const labelStyle = { fontSize: '14px', fontWeight: '600', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' };
    const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '15px', outline: 'none' };

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Saldo Disponível neste mês</p>
                        <h2 style={{ margin: '4px 0 0 0', fontSize: '32px', color: isBloqueado ? '#ef4444' : '#16a39c' }}>
                            R$ {saldoRestante > 0 ? saldoRestante.toFixed(2) : '0.00'}
                        </h2>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>Limite Mensal: <strong>R$ {limiteMensal.toFixed(2)}</strong></p>
                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>Utilizado: <strong>R$ {despesasDoMes.toFixed(2)}</strong></p>
                    </div>
                </div>
                <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(porcentagemGasta, 100)}%`, backgroundColor: isBloqueado ? '#ef4444' : '#16a39c', transition: 'width 0.5s ease-in-out' }}></div>
                </div>
            </div>

            {mensagem.texto && (
                <div style={{ padding: '16px', marginBottom: '16px', borderRadius: '8px', backgroundColor: mensagem.tipo === 'sucesso' ? '#dcfce7' : '#fee2e2', color: mensagem.tipo === 'sucesso' ? '#166534' : '#b91c1c' }}>
                    {mensagem.texto}
                </div>
            )}

            {isBloqueado ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: '#fef2f2', color: '#b91c1c', padding: '20px', borderRadius: '12px', border: '1px solid #fecaca' }}>
                    <AlertCircle size={24} />
                    <div>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Limite Mensal Atingido</h4>
                        <p style={{ margin: 0, fontSize: '14px' }}>O limite de R$ {limiteMensal.toFixed(2)} para esta loja foi atingido. Novos lançamentos estão bloqueados.</p>
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '32px', border: '1px solid #e2e8f0' }}>
                    <div style={{ margin: '0 0 24px 0', borderBottom: '2px solid #e0f2f1', paddingBottom: '12px' }}>
                        <h3 style={{ margin: 0, fontSize: '20px', color: '#0f172a' }}>
                            Registrar Nova Despesa
                        </h3>
                        {/* O "como fazer" ao lado do próprio lançamento — quem tem dúvida
                            do procedimento resolve aqui, sem sair da tela. Some sozinho
                            se o vídeo não estiver no ar. */}
                        <div style={{ marginTop: '12px' }}>
                            <VideoProcedimento
                                apiBase={API_VIDEOS}
                                chave="pettycash-lancamento"
                                cor={COR_FINANCEIRO}
                            />
                        </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                        
                        <div style={inputContainerStyle}>
                            <label style={labelStyle}><Store size={16} color="#16a39c" /> Loja</label>
                            <select 
                                name="loja" 
                                value={formData.loja} 
                                onChange={handleChange} 
                                style={{ ...inputStyle, backgroundColor: travadoNaLoja ? '#f1f5f9' : '#fff' }} 
                                required
                                disabled={travadoNaLoja}
                            >
                                <option value="">Selecione a Loja...</option>
                                {/* Preso à própria loja: garante a opção dele mesmo antes de a API responder */}
                                {travadoNaLoja && !lojas.find(l => l.NOME_LOJA === user.loja) && (
                                    <option value={user.loja}>{user.loja}</option>
                                )}
                                {lojas?.map((l, i) => <option key={i} value={l.NOME_LOJA}>{l.NOME_LOJA}</option>)}
                            </select>
                        </div>

                        <div style={inputContainerStyle}>
                            <label style={labelStyle}><Calendar size={16} color="#16a39c" /> Data da Compra</label>
                            <input type="date" name="data" value={formData.data} onChange={handleChange} min={primeiroDiaDoMes} style={inputStyle} required />
                        </div>

                        <div style={inputContainerStyle}>
                            <label style={labelStyle}><Tag size={16} color="#16a39c" /> Categoria da Despesa</label>
                            <select name="contaDespesa" value={formData.contaDespesa} onChange={handleChange} style={inputStyle} required>
                                <option value="">Selecione a Categoria...</option>
                                {categoriasDespesa.map((cat, i) => (
                                    <option key={i} value={cat.conta}>{cat.desc}</option>
                                ))}
                            </select>
                        </div>

                        <div style={inputContainerStyle}>
                            <label style={labelStyle}><Hash size={16} color="#16a39c" /> N° do Cupom / NFe</label>
                            <input type="text" name="numeroCupom" value={formData.numeroCupom} onChange={handleChange} placeholder="Ex: 123456" style={inputStyle} required />
                        </div>

                        <div style={{ display: 'flex', gap: '16px' }}>
                            <div style={{ ...inputContainerStyle, flex: 1 }}>
                                <label style={labelStyle}><DollarSign size={16} color="#16a39c" /> Valor (R$)</label>
                                <input type="number" step="0.01" name="valor" value={formData.valor} onChange={handleChange} placeholder="0.00" style={inputStyle} required />
                            </div>
                            <div style={{ ...inputContainerStyle, flex: '0 0 100px' }}>
                                <label style={labelStyle}><Layers size={16} color="#16a39c" /> Quantidade</label>
                                <input type="number" name="quantidade" value={formData.quantidade} onChange={handleChange} min="1" style={inputStyle} required />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '16px' }}>
                        <label style={labelStyle}>Anexar Cupom Fiscal</label>
                        {/* A área tracejada continua aceitando ARRASTAR o arquivo (no
                            desktop), mas o clique agora passa pelos dois gatilhos do
                            EscolherAnexo — era o input transparente por cima de tudo
                            que escondia a câmera no Android. */}
                        <div
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) setFormData(prev => ({ ...prev, cupom: f })); }}
                            style={{ marginTop: '8px', padding: '24px', border: '2px dashed #cbd5e1', borderRadius: '8px', textAlign: 'center', backgroundColor: '#f8fafc' }}
                        >
                            {formData.cupom ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#16a39c', marginBottom: '12px' }}>
                                    <CheckCircle2 size={32} style={{ marginBottom: '8px' }} />
                                    <span style={{ fontWeight: '600' }}>{formData.cupom.name}</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#64748b', marginBottom: '12px' }}>
                                    <UploadCloud size={32} color="#16a39c" style={{ marginBottom: '8px' }} />
                                    <span style={{ fontWeight: '600' }}>Arraste o cupom aqui ou use os botões abaixo</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <EscolherAnexo
                                    accept="image/*,.pdf"
                                    cor="#16a39c"
                                    rotulo={formData.cupom ? 'Trocar arquivo' : 'Escolher arquivo'}
                                    onArquivo={(file) => setFormData(prev => ({ ...prev, cupom: file }))}
                                />
                            </div>
                        </div>
                    </div>

                    <button type="submit" disabled={isCarregando} style={{ marginTop: '32px', width: '100%', padding: '14px', borderRadius: '8px', border: 'none', backgroundColor: isCarregando ? '#94a3b8' : '#16a39c', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: isCarregando ? 'not-allowed' : 'pointer' }}>
                        {isCarregando ? 'Enviando...' : 'Enviar para Aprovação'}
                    </button>
                </form>
            )}
        </div>
    );
};

export default FinanceiroPettyCashFormulario;